import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { authUpdateProfile } from '../api';
import { User, Mail, Phone, Save, LogOut, Calendar, Shield, Building2 } from 'lucide-react';

const Profile = () => {
  const { t, dir, lang } = useLang();
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', company_name: user?.company_name || '' });
  const [saving, setSaving] = useState(false);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await authUpdateProfile(form);
      setUser(updated);
      toast({ title: t('auth.updated') });
    } catch (err) {
      toast({ title: lang === 'ar' ? 'فشل التحديث' : 'Update failed', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const createdDate = user?.created_at ? new Date(user.created_at).toLocaleDateString(lang === 'ar' ? 'ar-AE' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <div className="pt-28 pb-16 min-h-screen" dir={dir}>
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb] mb-4">— {t('auth.account_settings')} —</div>
          <h1 className="font-display text-4xl md:text-5xl text-[#0a0a0a]">{t('auth.profile')}</h1>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Info Card */}
          <div className="glass-card rounded-2xl p-8 text-center">
            <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] flex items-center justify-center text-[#f8fafc] font-display text-4xl overflow-hidden shadow-lg shadow-[#2563eb]/20">
              {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.charAt(0)?.toUpperCase() || 'U')}
            </div>
            <div className="font-display text-xl text-[#0a0a0a] mt-4">{user?.name}</div>
            <div className="text-xs text-[#111827]/50 mt-1">{user?.email}</div>

            <div className="mt-6 pt-6 border-t border-[#2563eb]/15 space-y-3 text-start rtl:text-end text-xs">
              <div className="flex items-center gap-2 text-[#111827]/60">
                <Shield className="w-3.5 h-3.5 text-[#2563eb]" />
                <span>{t('auth.signed_via')}: <span className="text-[#3b82f6] uppercase">{user?.auth_provider}</span></span>
              </div>
              {createdDate && (
                <div className="flex items-center gap-2 text-[#111827]/60">
                  <Calendar className="w-3.5 h-3.5 text-[#2563eb]" />
                  <span>{t('auth.member_since')} {createdDate}</span>
                </div>
              )}
            </div>

            <Button onClick={logout} className="ghost-button w-full rounded-md h-10 mt-6 text-xs bg-transparent border-rose-400/30 text-rose-400 hover:bg-rose-500/10">
              <LogOut className="w-3.5 h-3.5 me-2" /> {t('auth.logout')}
            </Button>
          </div>

          {/* Edit Form */}
          <div className="md:col-span-2">
            <form onSubmit={onSave} className="glass-card rounded-2xl p-8 space-y-5">
              <h2 className="font-display text-2xl text-[#0a0a0a] mb-2">{t('auth.personal_info')}</h2>
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('auth.name')}</Label>
                <div className="relative">
                  <User className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                  <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" />
                </div>
              </div>
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('auth.email')}</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                  <Input value={user?.email || ''} disabled className="h-12 ps-10 bg-[#ffffff]/50 border-[#2563eb]/15 text-[#111827]/50 cursor-not-allowed" />
                </div>
              </div>
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">{t('auth.phone')}</Label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                  <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+971 ..." className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]" data-testid="profile-phone" />
                </div>
              </div>
              <div>
                <Label className="text-[#0a0a0a] text-xs uppercase tracking-widest mb-2 block">
                  {lang === 'ar' ? 'اسم الشركة' : 'Company name'}
                </Label>
                <div className="relative">
                  <Building2 className="w-4 h-4 text-[#2563eb]/60 absolute top-1/2 -translate-y-1/2 start-3 pointer-events-none" />
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder={lang === 'ar' ? 'مثلاً: iAccounting FZ-LLC' : 'e.g. iAccounting FZ-LLC'}
                    className="h-12 ps-10 bg-[#ffffff] border-[#2563eb]/25 text-[#111827]"
                    data-testid="profile-company"
                  />
                </div>
                <div className="mt-1 text-[10px] text-[#111827]/50">
                  {lang === 'ar' ? 'يُستخدم في دفاتر AI Accounting والإيصالات' : 'Used across your AI Accounting books and receipts'}
                </div>
              </div>
              <Button type="submit" disabled={saving} className="gold-button rounded-md h-11 px-6 text-sm">
                {saving ? '...' : <><Save className="w-4 h-4 me-2" /> {t('auth.save_changes')}</>}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
